import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { createTeacherSupportRequest } from "../services/TeacherAPIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherRequestInvoiceModal.css";

const REQUEST_TYPES = [
  {
    value: "Send a copy of our invoice",
    detail: "Use this if accounts needs another copy"
  },
  {
    value: "Add or update PO number",
    detail: "Use this if the invoice needs a purchase order"
  },
  {
    value: "Question about pricing",
    detail: "Use this if the invoice amount looks different"
  },
  {
    value: "Other",
    detail: "Add a short note on the next screen"
  }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const findMatchingOption = (options, value) => {
  const normalisedValue = normaliseComparableText(value);

  if (!normalisedValue) {
    return "";
  }

  return (
    options.find((option) => {
      const optionValue = normaliseComparableText(option.value);
      return optionValue === normalisedValue || optionValue.replace(/\s+/g, "-") === normalisedValue;
    })?.value || ""
  );
};

const isValidEmailDraft = (value) => {
  const email = normaliseText(value);

  return Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const normalisePrefill = (prefill) => ({
  invoiceRequestType: findMatchingOption(
    REQUEST_TYPES,
    prefill?.invoiceRequestType ||
      prefill?.invoice_request_type ||
      prefill?.requestType ||
      prefill?.request_type
  ),
  recipientEmail: normaliseText(prefill?.recipientEmail || prefill?.recipient_email || prefill?.email),
  poNumber: normaliseText(prefill?.poNumber || prefill?.po_number),
  note: normaliseText(prefill?.note)
});

function TeacherRequestInvoiceModal({
  show,
  handleClose,
  prefill = null,
  teacherEmail = "",
  schoolid = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete,
  onBackToTrialAccess = null
}) {
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const [stage, setStage] = useState("request");
  const [requestType, setRequestType] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  const trimmedRecipientEmail = normaliseText(recipientEmail);
  const trimmedPoNumber = normaliseText(poNumber);
  const trimmedNote = normaliseText(note);
  const requiresPoNumber = requestType === "Add or update PO number";
  const requiresNote = requestType === "Other" || requestType === "Question about pricing";
  const canReview =
    Boolean(requestType) &&
    isValidEmailDraft(trimmedRecipientEmail) &&
    (!requiresPoNumber || Boolean(trimmedPoNumber));
  const canPrepare = canReview && (!requiresNote || Boolean(trimmedNote));

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("request");
    setRequestType(prefillContext.invoiceRequestType);
    setRecipientEmail(prefillContext.recipientEmail || normaliseText(teacherEmail));
    setPoNumber(prefillContext.poNumber);
    setNote(prefillContext.note);
    setError("");
    setIsSubmitting(false);
    setIsDuplicatePending(false);
  }, [show, prefillContext, teacherEmail]);

  const returnToTeacherDashboard = () => {
    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: "",
        message: "Invoice request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const handleNext = () => {
    if (!requestType) {
      setError("Choose the invoice help you need.");
      return;
    }

    if (!isValidEmailDraft(trimmedRecipientEmail)) {
      setError("Add a valid recipient email.");
      return;
    }

    if (requiresPoNumber && !trimmedPoNumber) {
      setError("Add the PO number that should be used.");
      return;
    }

    setError("");
    setStage("review");
  };

  const handlePrepareRequest = async () => {
    if (!canPrepare) {
      setError(requiresNote ? "Add a short note before preparing this request." : "Check the invoice details.");
      return;
    }

    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = buildTeacherSupportRequestPayload({
      type: "billing_support",
      schoolId: schoolid,
      details: {
        invoiceRequestType: requestType,
        recipientEmail: trimmedRecipientEmail,
        poNumber: trimmedPoNumber,
        note: trimmedNote
      },
      source: {
        workflow: "request-invoice"
      }
    });

    try {
      const response = await createTeacherSupportRequest(payload);
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOption = (option) => {
    const selected = requestType === option.value;

    return (
      <button
        key={option.value}
        type="button"
        className={`request-invoice-option${selected ? " selected" : ""}`}
        onClick={() => {
          setRequestType(option.value);
          setError("");
        }}
        aria-pressed={selected}
      >
        <span className="request-invoice-radio" aria-hidden="true" />
        <span className="request-invoice-option-copy">
          <strong>{option.value}</strong>
          <span>{option.detail}</span>
        </span>
      </button>
    );
  };

  const renderSummary = () => (
    <div className="request-invoice-summary" aria-label="Invoice request summary">
      <span className="request-invoice-summary-row">
        <span>Request</span>
        <strong>{requestType || "Not selected"}</strong>
      </span>
      <span className="request-invoice-summary-row">
        <span>Send to</span>
        <strong>{trimmedRecipientEmail || "Not added"}</strong>
      </span>
      <span className="request-invoice-summary-row">
        <span>PO number</span>
        <strong>{trimmedPoNumber || "Not needed"}</strong>
      </span>
    </div>
  );

  const renderRequest = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Invoice Queries</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="request-invoice-intro">Tell us which invoice you need and where to send it.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="request-invoice-section">
          <Form.Label>What do you need?</Form.Label>
          <div className="request-invoice-options" aria-label="Invoice request type">
            {REQUEST_TYPES.map(renderOption)}
          </div>
        </Form.Group>

        <div className="request-invoice-section">
          <Form.Label as="div">Invoice details</Form.Label>
          <Form.Group controlId="teacherRequestInvoiceEmail">
            <Form.Text as="div" className="request-invoice-field-label">
              Recipient email
            </Form.Text>
            <Form.Control
              type="email"
              value={recipientEmail}
              onChange={(event) => {
                setRecipientEmail(event.target.value);
                setError("");
              }}
              aria-label="Recipient email"
            />
          </Form.Group>

          <Form.Group className="mt-3" controlId="teacherRequestInvoicePoNumber">
            <Form.Text as="div" className="request-invoice-field-label">
              PO number, if needed
            </Form.Text>
            <Form.Control
              value={poNumber}
              onChange={(event) => {
                setPoNumber(event.target.value);
                setError("");
              }}
              aria-label="PO number, if needed"
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="request-invoice-footer-note">
          {requestType ? `${requestType} selected.` : "Choose an invoice request first."}
        </span>
        {onBackToTrialAccess && (
          <Button variant="outline-secondary" onClick={onBackToTrialAccess}>
            Back to Trial Access Help
          </Button>
        )}
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={!canReview}>
          Next
        </Button>
      </Modal.Footer>
    </>
  );

  const renderReview = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Review Invoice Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="request-invoice-intro">Check the details before preparing the request.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="request-invoice-section">
          <Form.Label as="div">Request summary</Form.Label>
          {renderSummary()}
        </div>

        <Form.Group className="request-invoice-section" controlId="teacherRequestInvoiceNote">
          <Form.Label>Anything else?</Form.Label>
          <Form.Text as="div" className="request-invoice-field-label">
            Short note{requiresNote ? "" : " (optional)"}
          </Form.Text>
          <Form.Control
            as="textarea"
            rows={4}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setError("");
            }}
            aria-label="Short invoice request note"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="request-invoice-footer-note">
          {canPrepare ? "Invoice request ready." : "Add the required note."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("request")}>
          Back
        </Button>
        <Button variant="primary" onClick={handlePrepareRequest} disabled={!canPrepare || isSubmitting}>
          {isSubmitting ? "Sending..." : "Send request"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Invoice Request Sent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="request-invoice-intro">
          {isDuplicatePending
            ? "There is already an invoice request in progress for this school."
            : `The invoice request has been sent for ${trimmedRecipientEmail || "the selected recipient"}.`}
        </p>
        <Alert variant="success" className="request-invoice-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {isDuplicatePending
                ? "Your invoice request is already in progress."
                : "Your invoice request has been sent."}
            </strong>
            <span>ALPACA will review the invoice details and follow up.</span>
          </span>
        </Alert>
        <div className="request-invoice-next">
          <strong>What next?</strong>
          <span>
            {embedded
              ? "Close this workflow and return to SENSAI while ALPACA reviews the request."
              : "Return to the Help Centre while ALPACA reviews the request."}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="request-invoice-footer-note">Task complete.</span>
        {!embedded && (
          <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>
            Return to Teacher Dashboard
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Close
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-request-invoice-modal"
    >
      {stage === "request" && renderRequest()}
      {stage === "review" && renderReview()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherRequestInvoiceModal;
